import { useEffect, useState } from 'react';

import { Add, Contacts, Notifications } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useQuery, useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { If } from 'src/components/If';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { getWalletSummaryInfo } from 'src/services/wallet';

import { CreateWallet } from './CreateWallet';
import { useMonitorContext } from './MonitorContextProvider';
import { FollowingTab } from './components/FollowingTab';
import { GuestEnvironmentSelector } from './components/Guests/GuestEnvironmentSelector';
import { InvitesModal } from './components/Invites/InvitesModal';
import {
  MonitorDashboardNavigation,
  MonitorDashboardScope,
  MonitorWalletTab,
} from './components/MonitorDashboardNavigation';
import { MyWalletsTab } from './components/MyWalletsTab';
import { PendingWalletsTab } from './components/PendingWalletsTab';
import RecommendedFundsCarousel from './components/RecommendedFundsCarousel';
import { SharedWalletsTab } from './components/SharedWalletsTab';
import { UpgradeModal } from './components/UpgradeModal';
import { WalletSummaryCard } from './components/WalletSummaryCard';
import { useMonitorLimits } from './hooks/useMonitorLimits';

type TabValue = 'my-wallets' | 'shared' | 'pending' | 'following';

export default function Monitor() {
  const { auth } = useAuthContext();
  const [openCreateWallet, setOpenCreateWallet] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalType, setUpgradeModalType] = useState<'wallet' | 'fund'>(
    'wallet',
  );
  const [openInvitesModal, setOpenInvitesModal] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const hasGuestUserWalletEnvironments =
    !!auth?.user?.guestUserWalletEnvironments &&
    auth?.user?.guestUserWalletEnvironments?.length > 0;

  const { setGlobalFundsCount } = useMonitorContext();

  const {
    limits,
    monitorLimits,
    isLoadingMonitorLimits,
    refetchMonitorLimits,
    validateWalletCreation,
  } = useMonitorLimits();

  const getActiveTabFromUrl = (): TabValue => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as TabValue;
    return tab && ['my-wallets', 'shared', 'pending', 'following'].includes(tab)
      ? tab
      : 'my-wallets';
  };

  const [activeTab, setActiveTab] = useState<TabValue>(getActiveTabFromUrl());
  const [activeWalletTab, setActiveWalletTab] = useState<MonitorWalletTab>(
    () => {
      const initialTab = getActiveTabFromUrl();
      return initialTab === 'following' ? 'my-wallets' : initialTab;
    },
  );

  useEffect(() => {
    const tabFromUrl = getActiveTabFromUrl();
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    if (tabFromUrl !== 'following') {
      setActiveWalletTab(tabFromUrl);
    }
  }, [activeTab, location.search]);

  const navigateToTab = (newValue: TabValue) => {
    setActiveTab(newValue);
    if (newValue !== 'following') {
      setActiveWalletTab(newValue);
    }
    const params = new URLSearchParams(location.search);
    params.set('tab', newValue);
    history.push({ search: params.toString() });
  };

  const handleScopeChange = (scope: MonitorDashboardScope) => {
    navigateToTab(scope === 'following' ? 'following' : activeWalletTab);
  };
  const dataCards = {
    walletCount: { title: 'Total de Carteiras', description: 'ativas' },
    fundsCount: { title: 'Total de Fundos', description: 'monitorados' },
    quotaWarningCount: {
      title: 'Atenção necessária em',
      description: 'carteira(s)',
    },
  } as const;

  const {
    isLoading: isLoadingSummary,
    data: summaryCardsData,
    refetch: refetchSummaryInfo,
  } = useQuery({
    queryKey: ['wallets-summary-info'],
    queryFn: () => getWalletSummaryInfo(),
    onSuccess: (data) => {
      setGlobalFundsCount(data?.fundsCount || 0);
    },
    onError: (error) => {
      toast.error('Não foi possível carregar as informações da página.');
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const handleOpenCreateWallet = () => {
    if (isLoadingMonitorLimits || !monitorLimits) {
      refetchMonitorLimits();
      toast.info('Carregando limites do plano. Tente novamente em instantes.');
      return;
    }

    // Subscription usage from /wallet/monitor-limits — do not fall back to
    // summary walletCount (different scope: custom wallets only).
    const walletValidation = validateWalletCreation(limits.usageWallets);

    if (!walletValidation.isValid) {
      setUpgradeModalType('wallet');
      setShowUpgradeModal(true);
      return;
    }

    setOpenCreateWallet(true);
  };

  const handleFinish = () => {
    setOpenCreateWallet(false);
    refetchSummaryInfo();
    queryClient.invalidateQueries(['wallets']);
    refetchMonitorLimits();
  };

  const renderWalletSummaryCards = () => (
    <Box
      component="section"
      aria-label="Totalizadores das minhas carteiras"
      width="100%"
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      gap={2}
      px={3}
      py={2}>
      <WalletSummaryCard
        data={{
          title: dataCards.walletCount.title,
          description: dataCards.walletCount.description,
          value: summaryCardsData?.walletCount || 0,
          isLoading: isLoadingSummary,
          ...(limits.hasLimits
            ? {
                subtitle:
                  limits.maxWallets === null
                    ? 'Carteiras ilimitadas'
                    : `${limits.usageWallets}/${limits.maxWallets} usadas (assinatura)`,
              }
            : {}),
        }}
      />
      <WalletSummaryCard
        data={{
          title: dataCards.fundsCount.title,
          description: dataCards.fundsCount.description,
          value: summaryCardsData?.fundsCount || 0,
          isLoading: isLoadingSummary,
          ...(limits.hasLimits
            ? {
                subtitle:
                  limits.maxFunds === null
                    ? 'Fundos ilimitados por carteira'
                    : `Até ${limits.maxFunds} fundos por carteira`,
              }
            : {}),
        }}
      />
      <WalletSummaryCard
        data={{
          title: dataCards.quotaWarningCount.title,
          description: dataCards.quotaWarningCount.description,
          value: summaryCardsData?.quotaWarningCount || 0,
          isLoading: isLoadingSummary,
          ...(summaryCardsData?.quotaWarningCount > 0
            ? { color: '#ff4747' }
            : {}),
        }}
      />
    </Box>
  );

  const renderWalletTabContent = () => {
    switch (activeWalletTab) {
      case 'my-wallets':
        return (
          <>
            {renderWalletSummaryCards()}
            <MyWalletsTab />
          </>
        );
      case 'shared':
        return <SharedWalletsTab />;
      case 'pending':
        return <PendingWalletsTab />;
      default:
        return <MyWalletsTab />;
    }
  };

  return (
    <>
      <If condition={hasGuestUserWalletEnvironments}>
        <GuestEnvironmentSelector />
      </If>
      <Box mb={2}>
        <RecommendedFundsCarousel />
      </Box>
      <Box component="section">
        <Paper elevation={2}>
          <Box
            p={1}
            borderRadius={'5px'}
            display="flex"
            flexDirection="column"
            gap={2}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={4}>
              <Typography variant="h5">Dashboard de Carteiras</Typography>
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Notifications />}
                  onClick={() => history.push('/monitor/alerts')}>
                  Alertas Padrão do Usuário
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  onClick={handleOpenCreateWallet}>
                  Nova Carteira
                </Button>
                <IconButton
                  color="primary"
                  onClick={() => setOpenInvitesModal(true)}>
                  <Contacts sx={{ fontSize: 32 }} />
                </IconButton>
              </Box>
            </Box>
            <MonitorDashboardNavigation
              scope={activeTab === 'following' ? 'following' : 'wallets'}
              walletTab={activeWalletTab}
              isMobile={isMobile}
              onScopeChange={handleScopeChange}
              onWalletTabChange={navigateToTab}
              walletContent={renderWalletTabContent()}
              followingContent={<FollowingTab />}
            />
          </Box>
        </Paper>
      </Box>
      <CreateWallet
        open={openCreateWallet}
        onClose={() => setOpenCreateWallet(false)}
        onFinish={handleFinish}
      />
      <UpgradeModal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        type={upgradeModalType}
        maxLimit={
          upgradeModalType === 'wallet' ? limits.maxWallets : limits.maxFunds
        }
        currentCount={
          upgradeModalType === 'wallet'
            ? limits.usageWallets
            : summaryCardsData?.fundsCount || 0
        }
        userName={auth?.user?.name}
        userId={auth?.user?.id}
      />
      <InvitesModal
        open={openInvitesModal}
        onClose={() => setOpenInvitesModal(false)}
      />
    </>
  );
}
